import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { KeyCodes } from '@fluentui/react/lib/Utilities';
import { SurfaceFocusManager } from '../utils/SurfaceFocusManager';

export interface FocusManagedComponentProps {
    focusManager: SurfaceFocusManager;
    children?: React.ReactNode;
}

/**
 * A component that injects the given focusManager's behaviors onto a div and sets the content inside of it, allowing for injecting
 * focusManager behavior onto arbitrary components
 *
 * on*Capture events come down during the capture phase of the browser event rather than the bubbling phase, so children can't accidentally
 * stopPropagation on them and prevent the focusManager from seeing that an event occurred in one of the children.
 * see https://javascript.info/bubbling-and-capturing
 */
export default observer(function FocusManagedComponent(props: FocusManagedComponentProps) {
    const [boundEventHandlers] = React.useState<React.DOMAttributes<HTMLDivElement>>(() =>
        props.focusManager.registerEventHandlers({
            onKeyDown: function (
                this: SurfaceFocusManager,
                evt: React.KeyboardEvent<HTMLDivElement>
            ) {
                switch (evt.which) {
                    case KeyCodes.tab:
                        this.onTab(evt);
                        break;
                }
            },
            onMouseDownCapture: function (
                this: SurfaceFocusManager,
                evt: React.MouseEvent<HTMLDivElement>
            ) {
                this.onMouseDown(evt);
            },
            onFocusCapture: function (
                this: SurfaceFocusManager,
                evt: React.FocusEvent<HTMLDivElement>
            ) {
                if (
                    evt.currentTarget &&
                    evt.target.contains &&
                    (evt.target == evt.currentTarget || evt.currentTarget.contains(evt.target))
                ) {
                    this.onFocus(evt);
                }
            },
            onBlurCapture: function (
                this: SurfaceFocusManager,
                evt: React.FocusEvent<HTMLDivElement>
            ) {
                // only fire focusOut if leaving the parent, not if switching between children
                if (evt.target === evt.currentTarget) {
                    this.onFocusOut(evt);
                }
            },
        })
    );
    return <div {...boundEventHandlers}>{props.children}</div>;
});
